import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import DropPage from "@/pages/DropPage";
import Dashboard from "@/pages/Dashboard";
import LandingPage from "@/pages/LandingPage";
import Studio from "@/pages/Studio";
import Library from "@/pages/Library";
import Reels from "@/pages/Reels";
import Events from "@/pages/Events";
import AssociationsIndex from "@/pages/AssociationsIndex";
import AssociationDetail from "@/pages/AssociationDetail";
import OwnersIndex from "@/pages/OwnersIndex";
import OwnerDetail from "@/pages/OwnerDetail";
import DirectoryPage from "@/pages/DirectoryPage";
import Riders from "@/pages/Riders";
import RiderProfile from "@/pages/RiderProfile";
import Horses from "@/pages/Horses";
import HorseProfile from "@/pages/HorseProfile";
import TrackedRiders from "@/pages/TrackedRiders";
import ReelTemplates from "@/pages/ReelTemplates";
import SharePage from "@/pages/SharePage";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Bootstrap from "@/pages/Bootstrap";
import Galleries from "@/pages/Galleries";
import GalleryDetail from "@/pages/GalleryDetail";
import ClientTemplates from "@/pages/ClientTemplates";
import Users from "@/pages/Users";
import AdminBilling from "@/pages/AdminBilling";
import Team from "@/pages/Team";
import Jobs from "@/pages/Jobs";
import Watchlist from "@/pages/Watchlist";
import WatchNotificationBanner from "@/components/WatchNotificationBanner";
import DropNotificationBanner from "@/components/DropNotificationBanner";
import ClientPortal from "@/pages/ClientPortal";
import ClientMyClips from "@/pages/ClientMyClips";
import MyStudio from "@/pages/MyStudio";
import MyStudioEditor from "@/pages/MyStudioEditor";
import Upgrade from "@/pages/Upgrade";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ClientDashboard from "@/pages/ClientDashboard";
import { PortalShell } from "@/pages/ClientPortalShell";
import ClientGalleryDetail from "@/pages/ClientGalleryDetail";
import ShooterPortal from "@/pages/ShooterPortal";
import MediaUpload from "@/pages/MediaUpload";
import { ShooterList, ShooterGallery, MyPurchases } from "@/pages/Marketplace";
import AdminBroadcast from "@/pages/AdminBroadcast";
import BroadcastBanner from "@/components/BroadcastBanner";
import MobileShell from "@/pages/mobile/MobileShell";
import MobileLogin from "@/pages/mobile/MobileLogin";
import MobileLibrary from "@/pages/mobile/MobileLibrary";
import MobileReels from "@/pages/mobile/MobileReels";
import { Scissors, Film, LayoutGrid, Sparkles, Users as UsersIcon, Layers, Settings as SettingsIcon, Target, FolderHeart, LogOut, ShieldCheck, Activity, Eye, CalendarDays, Mic, Camera, Video as VideoIcon, Building2, UserSquare2, Search as SearchIcon, DollarSign, Megaphone } from "lucide-react";
import { GlobalSearchTrigger } from "@/components/GlobalSearch";

function Nav() {
  const { user, logout } = useAuth();
  const linkBase =
    "px-4 py-2 text-xs uppercase tracking-[0.2em] font-medium border-l-2 transition-colors flex items-center gap-3";
  const active = "border-orange-600 text-stone-100 bg-stone-900";
  const inactive = "border-transparent text-stone-500 hover:text-stone-200 hover:bg-stone-900/50";

  return (
    <aside
      className="hidden md:flex md:flex-col w-56 shrink-0 bg-stone-950 border-r border-stone-800 h-screen sticky top-0"
      data-testid="sidebar-nav"
    >
      <Link to="/dashboard" className="px-5 py-6 border-b border-stone-800 block" data-testid="brand-link">
        <div className="text-orange-600 text-[10px] tracking-[0.3em] uppercase font-bold">Rodeo</div>
        <div className="font-bold text-stone-100 uppercase tracking-tight text-xl leading-none mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Cut Studio
        </div>
      </Link>
      <div className="px-3 pt-3">
        <GlobalSearchTrigger />
      </div>
      <nav className="flex flex-col py-4">
        <NavLink to="/dashboard" end className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-dashboard">
          <LayoutGrid size={14} /> Dashboard
        </NavLink>
        <NavLink to="/studio" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-studio">
          <Scissors size={14} /> Studio
        </NavLink>
        <NavLink to="/library" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-library">
          <Film size={14} /> Library
        </NavLink>
        <NavLink to="/associations" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-associations">
          <Building2 size={14} /> Associations
        </NavLink>
        <NavLink to="/announcer" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-announcer">
          <Mic size={14} /> Announcer
        </NavLink>
        <NavLink to="/photographer" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-photographer">
          <Camera size={14} /> Photographer
        </NavLink>
        <NavLink to="/videographer" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-videographer">
          <VideoIcon size={14} /> Videographer
        </NavLink>
        <NavLink to="/reels" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-reels">
          <Sparkles size={14} /> Reels
        </NavLink>
        <NavLink to="/reel-templates" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-reel-templates">
          <Layers size={14} /> Templates
        </NavLink>
        <NavLink to="/galleries" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-galleries">
          <FolderHeart size={14} /> Galleries
        </NavLink>
        <NavLink to="/client-templates" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-client-templates">
          <Layers size={14} /> Client Templates
        </NavLink>
        <NavLink to="/riders" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-riders">
          <UsersIcon size={14} /> Riders
        </NavLink>
        <NavLink to="/horses" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-horses">
          <span className="text-base leading-none">🐎</span> Horses
        </NavLink>
        <NavLink to="/owners" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-owners">
          <UserSquare2 size={14} /> Owners
        </NavLink>
        <NavLink to="/tracked-riders" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-tracked-riders">
          <Target size={14} /> Tracked
        </NavLink>
        <NavLink to="/users" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-users">
          <ShieldCheck size={14} /> Users
        </NavLink>
        <NavLink to="/billing" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-billing">
          <DollarSign size={14} /> Billing
        </NavLink>
        <NavLink to="/broadcast" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-broadcast">
          <Megaphone size={14} /> Broadcast
        </NavLink>
        <NavLink to="/team" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-team">
          <UsersIcon size={14} /> Team
        </NavLink>
        <NavLink to="/jobs" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-jobs">
          <Activity size={14} /> Jobs
        </NavLink>
        <NavLink to="/watchlist" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-watchlist">
          <Eye size={14} /> Watchlist
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-settings">
          <SettingsIcon size={14} /> Settings
        </NavLink>
      </nav>
      <div className="mt-auto border-t border-stone-800">
        {user && (
          <div className="px-5 py-3 text-[11px]">
            <div className="text-stone-400 uppercase tracking-widest text-[10px]">Signed in as</div>
            <div className="text-stone-200 truncate" title={user.email}>{user.name || user.email}</div>
            <div className="text-orange-500/80 uppercase tracking-widest text-[9px] mt-0.5">{user.role}</div>
            <button
              onClick={async () => { await logout(); window.location.href = "/login"; }}
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-500 hover:text-rose-400 transition-colors"
              data-testid="nav-logout"
            >
              <LogOut size={11} /> Sign out
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function MobileNav() {
  const [showSearch, setShowSearch] = useState(false);
  return (
    <div className="md:hidden flex items-center justify-between bg-stone-950 border-b border-stone-800 px-4 py-3" data-testid="mobile-nav">
      <Link to="/dashboard" className="font-bold text-stone-100 uppercase tracking-tight text-lg" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
        <span className="text-orange-600">Rodeo</span> Cut Studio
      </Link>
      <div className="flex items-center gap-3 text-xs uppercase tracking-wider">
        <NavLink to="/dashboard" end className={({ isActive }) => isActive ? "text-orange-500" : "text-stone-400"} data-testid="mnav-dashboard">Home</NavLink>
        <NavLink to="/studio" className={({ isActive }) => isActive ? "text-orange-500" : "text-stone-400"} data-testid="mnav-studio">Studio</NavLink>
        <NavLink to="/library" className={({ isActive }) => isActive ? "text-orange-500" : "text-stone-400"} data-testid="mnav-library">Library</NavLink>
        <NavLink to="/galleries" className={({ isActive }) => isActive ? "text-orange-500" : "text-stone-400"} data-testid="mnav-galleries">Galleries</NavLink>
        <button
          onClick={() => setShowSearch(true)}
          className="text-stone-400 hover:text-orange-500 transition-colors"
          data-testid="mnav-search"
          aria-label="Search"
        >
          <SearchIcon size={16} />
        </button>
      </div>
      {showSearch && <MobileGlobalSearch onClose={() => setShowSearch(false)} />}
    </div>
  );
}

function MobileGlobalSearch({ onClose }) {
  return <GlobalSearchTrigger forceOpen={true} onForceClose={onClose} />;
}

export default function App() {
  return (
    <div className="bg-stone-950 text-stone-100 min-h-screen" style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }}>
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isShare = pathname.startsWith("/share/");
  const isLogin = pathname === "/login";
  const isSignup = pathname === "/signup";
  const isBootstrap = pathname === "/bootstrap";
  const isLanding = pathname === "/";
  const isPortal = pathname === "/portal" || pathname.startsWith("/portal/");
  const isMobile = pathname === "/m" || pathname.startsWith("/m/");
  const isDrop = pathname === "/drop";
  const isPayment = pathname.startsWith("/payment/");

  // Auto-redirect desktop routes → /m equivalent when opened on a phone.
  // Runs once on mount + whenever pathname changes; only for admin routes
  // that have a mobile counterpart. Ignores portal/share/drop pages.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isPhone = window.matchMedia("(max-width: 767px)").matches;
    if (!isPhone) return;
    if (isMobile || isShare || isPortal || isLanding || isDrop) return;
    if (isPayment) return;
    // Also skip login/bootstrap/signup
    if (isLogin || isBootstrap || isSignup) return;
    // Map desktop → mobile
    let target = null;
    if (pathname.startsWith("/library")) target = "/m/library";
    else if (pathname.startsWith("/reels")) target = "/m/reels";
    if (target) nav(target, { replace: true });
  }, [pathname, isMobile, isShare, isPortal, isLanding, isLogin, isBootstrap, isSignup, isDrop, nav]);

  if (isDrop) {
    return (
      <Routes>
        <Route path="/drop" element={<DropPage />} />
      </Routes>
    );
  }

  if (isMobile) {
    return (
      <Routes>
        <Route path="/m/login" element={<MobileLogin />} />
        <Route path="/m" element={<MobileShell />}>
          <Route index element={<MobileLibrary />} />
          <Route path="library" element={<MobileLibrary />} />
          <Route path="reels" element={<MobileReels />} />
        </Route>
      </Routes>
    );
  }

  if (isShare) {
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/share/:token" element={<SharePage />} />
        </Routes>
      </div>
    );
  }

  if (isLogin || isSignup) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
      </Routes>
    );
  }

  if (isBootstrap) {
    return (
      <Routes>
        <Route path="/bootstrap" element={<Bootstrap />} />
      </Routes>
    );
  }

  if (isLanding) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
      </Routes>
    );
  }

  if (isPayment) {
    return (
      <Routes>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
    );
  }

  if (isPortal) {
    // Photographers + videographers land on the shooter portal (their
    // marketplace dashboard). Regular clients land on My Clips.
    const shooterRoles = ["photographer", "videographer"];
    const isShooter = shooterRoles.includes(user?.role);
    return (
      <>
        <BroadcastBanner />
        <Routes>
        <Route
          path="/portal"
          element={
            <ProtectedRoute clientOnly>
              <Navigate to={isShooter ? "/portal/shooter" : "/portal/my-clips"} replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/shooter"
          element={
            <ProtectedRoute clientOnly>
              <ShooterPortal />
            </ProtectedRoute>
          }
        />
        {/* Mobile-optimized bulk media uploader for shooters (short URL for
            physical print / QR code handouts at events). */}
        <Route
          path="/mediaupload"
          element={
            <ProtectedRoute>
              <MediaUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/buy/photo"
          element={
            <ProtectedRoute clientOnly>
              <ShooterList kind="photo" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/buy/video"
          element={
            <ProtectedRoute clientOnly>
              <ShooterList kind="video" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/shooter/:uid"
          element={
            <ProtectedRoute clientOnly>
              <ShooterGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/purchases"
          element={
            <ProtectedRoute clientOnly>
              <MyPurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/my-clips"
          element={
            <ProtectedRoute clientOnly>
              <ClientMyClips />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/studio"
          element={
            <ProtectedRoute clientOnly>
              <MyStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/studio/:videoId"
          element={
            <ProtectedRoute clientOnly>
              <MyStudioEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/upgrade"
          element={
            <ProtectedRoute clientOnly>
              <Upgrade />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/subscription-success"
          element={
            <ProtectedRoute clientOnly>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/stats"
          element={
            <ProtectedRoute clientOnly>
              <PortalShell>
                <ClientDashboard />
              </PortalShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/gallery/:galleryId"
          element={
            <ProtectedRoute clientOnly>
              <ClientGalleryDetail />
            </ProtectedRoute>
          }
        />
        </Routes>
      </>
    );
  }

  const admin = (el) => <ProtectedRoute adminOnly>{el}</ProtectedRoute>;

  return (
    <div className="flex">
      {isAdmin && <Nav />}
      <div className="flex-1 min-w-0">
        {isAdmin && <MobileNav />}
        <BroadcastBanner />
        <WatchNotificationBanner />
        <DropNotificationBanner />
        <Routes>
          <Route path="/dashboard" element={admin(<Dashboard />)} />
          <Route path="/studio" element={admin(<Studio />)} />
          <Route path="/studio/:videoId" element={admin(<Studio />)} />
          <Route path="/library" element={admin(<Library />)} />
          <Route path="/performances/:eventName" element={admin(<Events />)} />
          <Route path="/events/:eventName" element={admin(<Events />)} />
          <Route path="/associations" element={admin(<AssociationsIndex />)} />
          <Route path="/associations/:name" element={admin(<AssociationDetail />)} />
          <Route path="/owners" element={admin(<OwnersIndex />)} />
          <Route path="/owners/:name" element={admin(<OwnerDetail />)} />
          <Route path="/announcer" element={admin(<DirectoryPage type="announcer" title="Announcers" subtitle="Shows, callers, and voices featured across your events." singularNoun="Announcer" />)} />
          <Route path="/photographer" element={admin(<DirectoryPage type="photographer" title="Photographers" subtitle="Photographers whose stills are tied to your events." singularNoun="Photographer" />)} />
          <Route path="/videographer" element={admin(<DirectoryPage type="videographer" title="Videographers" subtitle="Videographers and video partners covering your events." singularNoun="Videographer" />)} />
          <Route path="/reels" element={admin(<Reels />)} />
          <Route path="/riders" element={admin(<Riders />)} />
          <Route path="/rider/:name" element={admin(<RiderProfile />)} />
          <Route path="/horses" element={admin(<Horses />)} />
          <Route path="/horse/:name" element={admin(<HorseProfile />)} />
          <Route path="/tracked-riders" element={admin(<TrackedRiders />)} />
          <Route path="/reel-templates" element={admin(<ReelTemplates />)} />
          <Route path="/galleries" element={admin(<Galleries />)} />
          <Route path="/galleries/:galleryId" element={admin(<GalleryDetail />)} />
          <Route path="/client-templates" element={admin(<ClientTemplates />)} />
          <Route path="/users" element={admin(<Users />)} />
          <Route path="/billing" element={admin(<AdminBilling />)} />
          <Route path="/broadcast" element={admin(<AdminBroadcast />)} />
          <Route path="/team" element={admin(<Team />)} />
          <Route path="/jobs" element={admin(<Jobs />)} />
          <Route path="/watchlist" element={admin(<Watchlist />)} />
          <Route path="/settings" element={admin(<Settings />)} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}
